import { ReactElement } from 'react'
import { Box, Typography } from '@mui/material'

type Course = {
  id: string
  name: string
  imageName?: string
  status: 'Complete' | 'Incomplete'
  finalDate: string
}

type AssignmentThumbnailGridProps = {
  courses: Course[]
}

function getCourseImageUrl(imageName?: string): string {
  return `${import.meta.env.VITE_CLOUDFRONT_URL}/public/${imageName}`
}

export default function AssignmentThumbnailGrid({
  courses,
}: AssignmentThumbnailGridProps): ReactElement {
  let totalCourses = courses.length
  let displayCount = Math.min(totalCourses, 4)
  let remainingCount = totalCourses > 4 ? totalCourses - 4 : 0

  // Determine which courses to display
  let displayCourses = courses.slice(0, displayCount)

  // Handle different layouts based on course count
  if (totalCourses === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          paddingTop: '100%',
          position: 'relative',
          bgcolor: 'background.default',
          borderRadius: 2,
        }}
        role="img"
        aria-label="No course thumbnails"
      />
    )
  }

  if (totalCourses === 1) {
    // Single 1:1 image
    return (
      <Box
        sx={{
          width: '100%',
          paddingTop: '100%',
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
        }}
        role="img"
        aria-label={`Course thumbnail for ${displayCourses[0].name}`}
      >
        <Box
          component="img"
          src={getCourseImageUrl(displayCourses[0].imageName)}
          alt={displayCourses[0].name}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </Box>
    )
  }

  if (totalCourses === 2 || totalCourses === 3) {
    // Two images side by side (show first 2 even if there are 3)
    let showOverlay = totalCourses === 3
    let ariaLabel = showOverlay
      ? `Course thumbnails for ${displayCourses[0].name}, ${displayCourses[1].name}, and 1 more course`
      : `Course thumbnails for ${displayCourses[0].name} and ${displayCourses[1].name}`
    return (
      <Box
        sx={{
          width: '100%',
          paddingTop: '100%',
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
        }}
        role="img"
        aria-label={ariaLabel}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            gap: 0.5,
          }}
        >
          <Box
            component="img"
            src={getCourseImageUrl(displayCourses[0].imageName)}
            alt={displayCourses[0].name}
            sx={{
              width: '50%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Box sx={{ position: 'relative', width: '50%', height: '100%' }}>
            <Box
              component="img"
              src={getCourseImageUrl(displayCourses[1].imageName)}
              alt={displayCourses[1].name}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: showOverlay ? 'brightness(0.6)' : 'none',
              }}
            />
            {showOverlay && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0.4)',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                  }}
                  aria-hidden="true"
                >
                  +1
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    )
  }

  // 4+ courses: Show 2x2 grid
  let showOverlay = totalCourses > 4
  let courseNames = displayCourses.map((c) => c.name).join(', ')
  let gridAriaLabel = showOverlay
    ? `Course thumbnails for ${courseNames}, and ${remainingCount} more courses`
    : `Course thumbnails for ${courseNames}`
  return (
    <Box
      sx={{
        width: '100%',
        paddingTop: '100%',
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
      }}
      role="img"
      aria-label={gridAriaLabel}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gap: 0.5,
        }}
      >
        {displayCourses.map((course, index) => {
          let isLast = index === 3
          return (
            <Box key={course.id} sx={{ position: 'relative' }}>
              <Box
                component="img"
                src={getCourseImageUrl(course.imageName)}
                alt={course.name}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: isLast && showOverlay ? 'brightness(0.6)' : 'none',
                }}
              />
              {isLast && showOverlay && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0, 0, 0, 0.4)',
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: 'white',
                    }}
                    aria-hidden="true"
                  >
                    +{remainingCount}
                  </Typography>
                </Box>
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
